import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'

import NotificationSnackbar from '../ui/NotificationSnackbar'
import { CrmBadge, CrmButton, CrmCheckbox, CrmModal } from '../ui'
import SingleRecordWorkspace from '../ui/SingleRecordWorkspace'
import QuotationItemsSection from './QuotationItemsSection'
import ChannelSelectModal from '../ui/ChannelSelectModal'
import AddLeadDialog from '../leads/AddLeadDialog'
import QuotationProductPickerDrawer from './QuotationProductPickerDrawer'
import { CheckCircleOutline, FileCopyOutlined, PictureAsPdfOutlined, ReceiptLongOutlined, SaveOutlined, ShareOutlined } from '@mui/icons-material'
import {
  fetchQuotationById,
  createQuotation,
  updateQuotation,
  updateQuotationStatus,
  updateQuotationItems,
  sendQuotationEmailToCustomer,
  sendQuotationWhatsAppToCustomer,
  generateQuotationPdf,
  fetchQuotationApprovalPreview,
} from '../../services/quotationService'
import { fetchLeads } from '../../services/leadService'
import { createInvoiceFromOrder, createProformaFromQuotation } from '../../services/invoiceService'
import { useSettings } from '../../context/SettingsContext'
import { displayCurrency } from '../../utils/currencyUtils'
import { calculateQuotationTotals } from '../../utils/quotationCalculator'
import QuotationSummary from './QuotationSummary'
import QuotationPackageManager from './QuotationPackageManager'
import { applyPackageDefinitions, packagesFromItems } from '../../utils/quotationPackages'

import {
  fetchAllProducts,
} from '../../services/productServices'

import '../../assets/styles/QuotationDetail.scss'

import '../../assets/styles/LeadsTable.scss'
import { formatQty, formatMoney } from '../../utils/formatters'

/* ---------------------------------------
   CONSTANTS — MUST MATCH DB ENUMS
--------------------------------------- */
const COST_PRICING_MODES = ['absolute', 'percentage']

const eventChronologyError = (meta = {}) => {
  const startDate = String(meta.event_start_date || '').slice(0, 10)
  const endDate = String(meta.event_end_date || '').slice(0, 10)
  const startTime = String(meta.event_start_time || '').slice(0, 5)
  const endTime = String(meta.event_end_time || '').slice(0, 5)
  if (startDate && endDate && endDate < startDate) return 'End date cannot be before the start date.'
  if (startDate && endDate && startDate === endDate && startTime && endTime && endTime < startTime) return 'End time cannot be before the start time on the same day.'
  return ''
}

function QuotationDetail() {
  const { id } = useParams()
  const navigate = useNavigate()

  const { settings } = useSettings()
  const currency = displayCurrency(settings?.currency_code)
  const isGeneralBusiness = String(settings?.business_type || 'GENERAL').toUpperCase() === 'GENERAL'
  const gstPricingMode = settings?.gst_pricing_mode || 'INCLUSIVE'
  const [products, setProducts] = useState([])
  const [overallDiscount, setOverallDiscount] = useState(0)
  const savedFingerprintRef = useRef('')
  const [approvalDialog, setApprovalDialog] = useState({ open: false, loading: false, preview: null })
  const [approving, setApproving] = useState(false)
  const [downloadingPdf, setDownloadingPdf] = useState(false)
  const [pendingNavigation, setPendingNavigation] = useState(null)
  const allowNavigationRef = useRef(false)

  const [quotation, setQuotation] = useState(null)
  const [loading, setLoading] = useState(true)
  const [pax, setPax] = useState(1)
  const [multiplyByPax, setMultiplyByPax] = useState(false) // retained only to read legacy rows before migration
  const [packages, setPackages] = useState([])
  const [leads, setLeads] = useState([])
  const [openAddLeadDialog, setOpenAddLeadDialog] = useState(false)

  const loadLeads = useCallback(async () => {
    try {
      const res = await fetchLeads()
      const leadsArray =
        Array.isArray(res?.leads) ? res.leads :
          Array.isArray(res?.data) ? res.data :
            []

      setLeads(leadsArray)
    } catch (err) {
      console.error('Failed to load leads', err)
    }
  }, [])


  const [cateringMeta, setCateringMeta] = useState({
    event_name: '',
    event_date: '',
    event_time: '',
    event_start_date: '',
    event_start_time: '',
    event_end_date: '',
    event_end_time: '',
    event_location: ''
  })


  const [headerForm, setHeaderForm] = useState({
    lead_id: '',
    quotation_date: '',
    valid_until: '',
    notes: ''
  })

  const isLocked = quotation?.is_locked === 1 || ['approved', 'converted'].includes(String(quotation?.status || '').toLowerCase())


  const [items, setItems] = useState([])
  const [openProductDialog, setOpenProductDialog] = useState(false)

  const [notif, setNotif] = useState({
    open: false,
    message: '',
    severity: 'success'
  })

  const [selectedLead, setSelectedLead] = useState(null)
  const [channelModalOpen, setChannelModalOpen] = useState(false)
  const [invoiceDialog, setInvoiceDialog] = useState({ open: false, proforma: true, tax: false, loading: false })

  const currentFingerprint = useMemo(() => JSON.stringify({
    headerForm,
    cateringMeta,
    pax: Number(pax || 0),
    multiplyByPax,
    overallDiscount: Number(overallDiscount || 0),
    packages: packages.map((pkg) => ({ key: pkg.key, name: pkg.name, pax: Number(pkg.pax || 0), pricing_mode: pkg.pricing_mode })),
    items: (items || []).map((item) => ({
      product_id: item.product_id ?? item.product?.id ?? null,
      quantity: Number(item.quantity || 0),
      selling_price: Number(item.selling_price || 0),
      discount: Number(item.discount || 0),
      gst_rate: Number(item.gst_rate || 0),
      package_key: item.package_key || null,
    })),
  }), [headerForm, cateringMeta, pax, multiplyByPax, overallDiscount, packages, items])
  const isDirty = Boolean(quotation && !isLocked && savedFingerprintRef.current && currentFingerprint !== savedFingerprintRef.current)

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!isDirty || allowNavigationRef.current) return
      event.preventDefault()
      event.returnValue = ''
    }

    const handleDocumentNavigation = (event) => {
      if (!isDirty || allowNavigationRef.current || event.defaultPrevented) return
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

      const anchor = event.target?.closest?.('a[href]')
      if (!anchor || anchor.target === '_blank' || anchor.hasAttribute('download')) return

      const href = anchor.getAttribute('href')
      if (!href || href.startsWith('#') || href.startsWith('javascript:')) return

      const targetUrl = new URL(anchor.href, window.location.href)
      if (targetUrl.href === window.location.href) return

      event.preventDefault()
      event.stopPropagation()
      setPendingNavigation({ href: `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`, external: targetUrl.origin !== window.location.origin })
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    document.addEventListener('click', handleDocumentNavigation, true)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      document.removeEventListener('click', handleDocumentNavigation, true)
    }
  }, [isDirty])

  const leaveQuotationPage = useCallback((destination = pendingNavigation) => {
    if (!destination) return
    allowNavigationRef.current = true
    setPendingNavigation(null)
    if (destination.external) {
      window.location.assign(destination.href)
      return
    }
    navigate(destination.href)
  }, [navigate, pendingNavigation])

  /* ---------------------------------------
     HELPERS
  --------------------------------------- */
  const showNotification = useCallback((message, severity = 'success') => {
    setNotif({ open: true, message, severity })
  }, [])

  const normalizeCostMode = (mode) =>
    COST_PRICING_MODES.includes(mode) ? mode : 'absolute'

  /**
   * Single source of truth for UPDATE / VERSION payload
   * Backend expects FULL snapshot fields always.
   */
  const buildSnapshotItemsPayload = useCallback(() => {
    return applyPackageDefinitions(items || [], packages)
      .map((i) => {
        const product_id = i.product_id ?? i.product?.id ?? null
        const product_name =
          i.product_name ||
          i.product?.name ||
          i.product?.label ||
          i.product?.title ||
          '[Unnamed Product]'

        return {
          product_id,
          product_name,

          variant_id: i.variant_id ?? null,
          variant_sku: i.variant_sku || '',

          quantity: Number(i.quantity) || 0,

          // COST SNAPSHOT
          cost_price: Number(i.cost_price) || 0,
          cost_price_qty: Number(i.cost_price_qty) || 1,
          cost_price_unit: i.cost_price_unit || 'unit',
          cost_unit: i.cost_unit || i.cost_price_unit || 'unit',
          cost_pricing_mode: normalizeCostMode(i.cost_pricing_mode),
          cost_discount_percent: Number(i.cost_discount_percent) || 0,

          // SELLING SNAPSHOT
          unit_price: Number(i.selling_price) || 0,
          discount: Number(i.discount) || 0,
          tax: Number(i.tax) || 0,

          // JSON SNAPSHOTS
          attributes_json: i.attributes_json || {},
          packaging_json: i.packaging_json || {},

          package_key: i.package_key || null,
          package_name: i.package_name || null,
          package_pax: i.package_pax || null,
          package_pricing_mode: i.package_pricing_mode || 'ITEM_QUANTITY',
          package_sort_order: i.package_sort_order ?? null
        }

      })
      // Keep only valid rows (backend will reject otherwise)
      .filter((i) => i.product_id && i.product_name && i.quantity > 0)
  }, [items, packages])

  /* ---------------------------------------
     PRODUCT → QUOTATION SNAPSHOT
  --------------------------------------- */
  const handleProductSelect = (index, product) => {
    if (!product) {
      updateItem(index, {
        product: null,
        product_id: null,
        product_name: '',
        variant_id: null,
        variant_sku: '',
        selling_price: 0,
        selling_price_qty: 1,
        selling_price_unit: 'unit',
        cost_price: 0,
        cost_price_qty: 1,
        cost_price_unit: 'unit',
        cost_unit: 'unit',
        cost_pricing_mode: 'absolute',
        cost_discount_percent: 0,
        attributes_json: {},
        packaging_json: {}
      })
      return
    }

    const productId = product.id ?? product.product_id ?? null
    const productName = product.name ?? product.label ?? product.title ?? ''
    const sellingPrice = Number(product.selling_price ?? product.price ?? 0) || 0
    const costPrice = Number(product.cost_price ?? product.cost ?? 0) || 0

    const variantId =
      product.variant_id ??
      product.variantId ??
      product.variant?.id ??
      null

    const variantSku =
      product.variant_sku ??
      product.sku ??
      product.variant?.sku ??
      ''

    updateItem(index, {
      product,
      product_id: productId,
      product_name: productName || '[Unnamed Product]',

      // COST SNAPSHOT
      cost_price: costPrice,
      cost_price_qty: Number(product.cost_price_qty ?? 1) || 1,
      cost_price_unit: product.cost_price_unit || 'unit',
      cost_unit: product.cost_unit || product.cost_price_unit || 'unit',
      cost_pricing_mode: normalizeCostMode(product.cost_pricing_mode),
      cost_discount_percent: Number(product.cost_discount_percent ?? 0) || 0,

      // SELLING SNAPSHOT
      selling_price: sellingPrice,
      selling_price_qty: Number(product.selling_price_qty ?? 1) || 1,
      selling_price_unit: product.selling_price_unit || 'unit',

      // IDENTITY SNAPSHOT
      variant_id: variantId,
      variant_sku: variantSku,
      attributes_json: product.attributes_json || {},
      packaging_json: product.packaging_json || {}
    })
  }

  useEffect(() => {
    fetchAllProducts()
      .then(res => {
        const list =
          Array.isArray(res) ? res :
            Array.isArray(res?.data) ? res.data :
              Array.isArray(res?.products) ? res.products : []
        setProducts(list)
      })
      .catch(err => console.error('Failed to load products', err))
  }, [])

  useEffect(() => {
    if (quotation) {
      setOverallDiscount(
        Number(quotation.quotation_discount_value ?? quotation.quotation_discount_amount ?? 0)
      )
    }
  }, [quotation])



  const loadQuotation = useCallback(async () => {
    try {
      setLoading(true)
      const data = await fetchQuotationById(id)
      setQuotation(data)
      setMultiplyByPax(Boolean(Number(data.pax_multiplier_enabled || 0)))
        if (data.quotation_mode === 'CATERING') {
        setPax(Number(data.pax) || 1)
        setCateringMeta({
          event_name: data.event_name || '',
          event_date: data.event_date ? (data.event_date.match(/^\d{4}-\d{2}-\d{2}/) ? data.event_date.substring(0, 10) : data.event_date) : '',
          event_time: data.event_time || '',
          event_start_date: data.event_start_date ? (data.event_start_date.match(/^\d{4}-\d{2}-\d{2}/) ? data.event_start_date.substring(0, 10) : data.event_start_date) : '',
          event_start_time: data.event_start_time || '',
          event_end_date: data.event_end_date ? (data.event_end_date.match(/^\d{4}-\d{2}-\d{2}/) ? data.event_end_date.substring(0, 10) : data.event_end_date) : '',
          event_end_time: data.event_end_time || '',
          event_location: data.event_location || ''
        })
      }
      // HEADER
      setHeaderForm({
        lead_id: data.lead_id ?? '',
        quotation_date: data.quotation_date ? (data.quotation_date.match(/^\d{4}-\d{2}-\d{2}/) ? data.quotation_date.substring(0, 10) : data.quotation_date) : '',
        valid_until: data.valid_until ? (data.valid_until.match(/^\d{4}-\d{2}-\d{2}/) ? data.valid_until.substring(0, 10) : data.valid_until) : '',
        notes: data.notes ?? ''
      })

      // ITEMS (STRICT NORMALIZATION)
      const normalizedItems = (data.items || []).map((item) => ({
        ...item,

        product: item.product_id
          ? { id: item.product_id, name: item.product_name }
          : null,

        product_id: item.product_id ?? null,
        product_name: item.product_name || (item.product_id ? '[Unnamed Product]' : ''),

        variant_id: item.variant_id ?? null,
        variant_sku: item.variant_sku || '',

        quantity: Number(item.quantity) || 1,

        cost_price: Number(item.cost_price) || 0,
        cost_price_qty: Number(item.cost_price_qty) || 1,
        cost_price_unit: item.cost_price_unit || 'unit',
        cost_unit: item.cost_unit || item.cost_price_unit || 'unit',
        cost_pricing_mode: normalizeCostMode(item.cost_pricing_mode),
        cost_discount_percent: Number(item.cost_discount_percent) || 0,

        selling_price: Number(item.selling_price) || 0,
        selling_price_qty: Number(item.selling_price_qty) || 1,
        selling_price_unit: item.selling_price_unit || 'unit',

        attributes_json: item.attributes_json || {},
        packaging_json: item.packaging_json || {},

        discount: Number(item.discount) || 0,
        tax: Number(item.tax) || 0
      }))

      setItems(normalizedItems)
      const loadedPackages = packagesFromItems(normalizedItems)
      setPackages(loadedPackages)
      savedFingerprintRef.current = JSON.stringify({
        headerForm: {
          lead_id: data.lead_id ?? '',
          quotation_date: data.quotation_date ? String(data.quotation_date).slice(0, 10) : '',
          valid_until: data.valid_until ? String(data.valid_until).slice(0, 10) : '',
          notes: data.notes ?? ''
        },
        cateringMeta: {
          event_name: data.event_name || '',
          event_date: data.event_date ? String(data.event_date).slice(0, 10) : '',
          event_time: data.event_time || '',
          event_start_date: data.event_start_date ? String(data.event_start_date).slice(0, 10) : '',
          event_start_time: data.event_start_time || '',
          event_end_date: data.event_end_date ? String(data.event_end_date).slice(0, 10) : '',
          event_end_time: data.event_end_time || '',
          event_location: data.event_location || ''
        },
        pax: Number(data.pax || 1),
        multiplyByPax: Boolean(Number(data.pax_multiplier_enabled || 0)),
        overallDiscount: Number(data.quotation_discount_value ?? data.quotation_discount_amount ?? 0),
        packages: loadedPackages.map((pkg) => ({ key: pkg.key, name: pkg.name, pax: Number(pkg.pax || 0), pricing_mode: pkg.pricing_mode })),
        items: normalizedItems.map((item) => ({
          product_id: item.product_id ?? item.product?.id ?? null,
          quantity: Number(item.quantity || 0),
          selling_price: Number(item.selling_price || 0),
          discount: Number(item.discount || 0),
          gst_rate: Number(item.gst_rate || 0),
          package_key: item.package_key || null,
        })),
      })

      // LEAD
      if (data.lead_id) {
        const leadsRes = await fetchLeads()
        const leads = Array.isArray(leadsRes?.leads)
          ? leadsRes.leads
          : Array.isArray(leadsRes?.data)
            ? leadsRes.data
            : []

        setSelectedLead(
          leads.find((l) => String(l.id) === String(data.lead_id)) || null
        )
      } else {
        setSelectedLead(null)
      }
    } catch (err) {
      console.error(err)
      showNotification('Failed to load quotation', 'error')
    } finally {
      setLoading(false)
    }
  }, [id, showNotification])

  /* ---------------------------------------
     LOAD QUOTATION
  --------------------------------------- */
  useEffect(() => {
    loadQuotation()
    loadLeads()
  }, [loadQuotation, loadLeads])

  /* ---------------------------------------
     HEADER SAVE
  --------------------------------------- */
  /* ---------------------------------------
     ITEMS
  --------------------------------------- */
  const addItem = () => {
    setItems((prev) => [
      ...prev,
      {
        product: null,
        product_id: null,
        product_name: '',
        variant_id: null,
        variant_sku: '',

        quantity: 1,

        cost_price: 0,
        cost_price_qty: 1,
        cost_price_unit: 'unit',
        cost_unit: 'unit',
        cost_pricing_mode: 'absolute',
        cost_discount_percent: 0,

        selling_price: 0,
        selling_price_qty: 1,
        selling_price_unit: 'unit',

        attributes_json: {},
        packaging_json: {},

        discount: 0,
        tax: 0,
        package_key: packages[0]?.key || null
      }
    ])
  }

  const updateItem = (index, updates) => {
    setItems((prev) => {
      const copy = [...prev]
      copy[index] = { ...copy[index], ...updates }
      return copy
    })
  }

  const handleSubmit = async () => {
    try {
      if (!headerForm.lead_id) {
        showNotification('Contact is required', 'warning')
        return false
      }
      if (!headerForm.quotation_date) {
        showNotification('Quotation date is required', 'warning')
        return false
      }

      if (quotation?.quotation_mode === 'CATERING' && (!pax || pax < 1)) {
        showNotification('PAX is required for catering', 'warning')
        return false
      }
      if (quotation?.quotation_mode === 'CATERING') {
        const chronologyError = eventChronologyError(cateringMeta)
        if (chronologyError) {
          showNotification(chronologyError, 'warning')
          return false
        }
      }

      const emptyPackage = packages.find((pkg) => !items.some((item) => String(item.package_key || '') === String(pkg.key)))
      if (emptyPackage) {
        showNotification(`Add a product to ${emptyPackage.name || 'the package'} before saving`, 'warning')
        return false
      }
      const payloadItems = buildSnapshotItemsPayload()
      if (!payloadItems.length) {
        showNotification('Add at least one valid item', 'warning')
        return false
      }

      // 1) Save items snapshot
      await updateQuotationItems(id, payloadItems)

      // 2) Save header + discount + totals (+ catering meta)
      await updateQuotation(id, {
        ...headerForm,

        quotation_discount_type: 'FLAT',
        quotation_discount_value: Number(overallDiscount || 0),

        ...(quotation?.quotation_mode === 'CATERING' && {
          pax,
          pax_multiplier_enabled: false,
          event_name: cateringMeta.event_name || null,
          event_location: cateringMeta.event_location || null,
          event_start_date: cateringMeta.event_start_date || null,
          event_start_time: cateringMeta.event_start_time || null,
          event_end_date: cateringMeta.event_end_date || null,
          event_end_time: cateringMeta.event_end_time || null
        })
      })


      showNotification('Quotation updated successfully')
      await loadQuotation()
      return true
    } catch (err) {
      console.error(err)
      showNotification(
        err?.response?.data?.error || 'Failed to save quotation',
        'error'
      )
      return false
    }
  }



  /* ---------------------------------------
     VERSION
  --------------------------------------- */
  const handleCreateVersion = async () => {
    try {
      const emptyPackage = packages.find((pkg) => !items.some((item) => String(item.package_key || '') === String(pkg.key)))
      if (emptyPackage) return showNotification(`Add a product to ${emptyPackage.name || 'the package'} before creating a version`, 'warning')
      const payloadItems = buildSnapshotItemsPayload()

      if (!payloadItems.length) {
        return showNotification('Add at least one valid item', 'warning')
      }

      // const nextVersion = Number(quotation.version || 1) + 1

      const payload = {
        parent_id: quotation.parent_id || quotation.id,
        lead_id: headerForm.lead_id,
        quotation_date: headerForm.quotation_date,
        valid_until: headerForm.valid_until || null,
        notes: headerForm.notes || null,
        items: payloadItems,
        ...(quotation.quotation_mode === 'CATERING' && {
          pax,
          pax_multiplier_enabled: false,
          event_name: cateringMeta.event_name || null,
          event_location: cateringMeta.event_location || null,
          event_start_date: cateringMeta.event_start_date || null,
          event_start_time: cateringMeta.event_start_time || null,
          event_end_date: cateringMeta.event_end_date || null,
          event_end_time: cateringMeta.event_end_time || null
        })
      }


      const res = await createQuotation(payload)

      showNotification(`New Version created`)
      navigate(`/quotations/${res.id}`)
    } catch (err) {
      console.error(err)
      showNotification(
        err?.response?.data?.error || 'Failed to create version',
        'error'
      )
    }
  }


  const openInvoiceDialog = () => {
    if (!['approved', 'converted'].includes(String(quotation?.status || '').toLowerCase())) {
      showNotification('Invoices can only be generated from an approved quotation', 'warning')
      return
    }
    setInvoiceDialog({ open: true, proforma: true, tax: false, loading: false })
  }

  const generateSelectedInvoices = async () => {
    if (!invoiceDialog.proforma && !invoiceDialog.tax) {
      showNotification('Select Proforma Invoice, Tax Invoice, or both.', 'warning')
      return
    }
    const relatedOrder = (quotation?.orders || [])[0]
    if (invoiceDialog.tax && !relatedOrder?.id) {
      showNotification('A related unified Order is required before generating a Tax Invoice.', 'warning')
      return
    }
    setInvoiceDialog((current) => ({ ...current, loading: true }))
    try {
      const jobs = []
      if (invoiceDialog.tax) jobs.push(createInvoiceFromOrder(relatedOrder.id))
      if (invoiceDialog.proforma) jobs.push(createProformaFromQuotation(quotation.id))
      await Promise.all(jobs)
      showNotification(`${invoiceDialog.proforma && invoiceDialog.tax ? 'Proforma and Tax Invoice' : invoiceDialog.proforma ? 'Proforma Invoice' : 'Tax Invoice'} generated successfully.`)
      setInvoiceDialog({ open: false, proforma: true, tax: false, loading: false })
      await loadQuotation()
    } catch (err) {
      showNotification(err?.response?.data?.error || 'Failed to generate invoice document.', 'error')
      setInvoiceDialog((current) => ({ ...current, loading: false }))
    }
  }

  const handleSendQuotationEmail = async () => {
    try {
      await sendQuotationEmailToCustomer(quotation.id)
      showNotification('Quotation email sent successfully')
    } catch (err) {
      console.error(err)
      showNotification(
        err?.response?.data?.error || 'Failed to send quotation email',
        'error'
      )
    }
  }

  const handleSendQuotationWhatsApp = async () => {
    try {
      await sendQuotationWhatsAppToCustomer(quotation.id)
      showNotification('Quotation WhatsApp sent successfully')
    } catch (err) {
      console.error(err)
      showNotification(
        err?.response?.data?.error || 'Failed to send quotation WhatsApp',
        'error'
      )
    }
  }

  const reorderItems = (from, to) => {
    if (from === to || from == null || to == null) return

    setItems(prev => {
      const copy = [...prev]
      const [moved] = copy.splice(from, 1)
      copy.splice(to, 0, moved)
      return copy
    })
  }



  /* ---------------------------------------
     TOTALS
  --------------------------------------- */
  const pricedItems = useMemo(() => applyPackageDefinitions(items, packages), [items, packages])
  const totals = calculateQuotationTotals({
    items: pricedItems,
    overallDiscount,
    pax: 1,
    quotationMode: 'GENERAL',
    gstPricingMode
  })
  console.log(quotation)

  const customerName = (
    `${(quotation?.first_name || selectedLead?.first_name || '') + ' ' + (quotation?.last_name || selectedLead?.last_name || '')}`.trim()
  ) || quotation?.company_name || selectedLead?.company_name || 'Customer'
  const eventName = quotation?.catering?.event_name || quotation?.event_name || ''
  const itemSummaries = (items || []).map(it => {
    const name = it.product_name || it.product?.name || it.title || 'Item'
    const qty = formatQty(it.quantity || it.qty || 1)
    const amt = formatMoney(it.line_total || it.lineTotal || (Number(it.selling_price || it.unit_price || 0) * Number(it.quantity || it.qty || 0)))
    return `${name} x${qty} · ${amt}`
  })
  const visible = itemSummaries.slice(0, 5)
  const briefSummary = [
    eventName,
    visible.join(' · '),
    itemSummaries.length > 5 ? `+${itemSummaries.length - 5} more` : ''
  ].filter(Boolean).join(' · ')




  const handleDownloadPdf = async () => {
    if (downloadingPdf || !quotation?.id) return;
    try {
      setDownloadingPdf(true);
      await generateQuotationPdf(quotation.id);
    } catch (error) {
      showNotification(error?.response?.data?.error || 'Failed to download quotation PDF', 'error');
    } finally {
      setDownloadingPdf(false);
    }
  }

  const openApprovalDialog = async () => {
    setApprovalDialog({ open: true, loading: true, preview: null });
    try {
      const preview = await fetchQuotationApprovalPreview(quotation.id);
      setApprovalDialog({ open: true, loading: false, preview });
    } catch (error) {
      setApprovalDialog({ open: true, loading: false, preview: { error: error?.response?.data?.error || 'Could not resolve workflow settings.' } });
    }
  }

  const confirmApproval = async () => {
    try {
      setApproving(true);
      const result = await updateQuotationStatus(quotation.id, 'approved');
      setApprovalDialog({ open: false, loading: false, preview: null });
      await loadQuotation();
      const createdBits = [
        result?.order?.order_number ? `Order ${result.order.order_number}` : null,
        result?.kot?.id ? `KOT #${result.kot.id}` : null,
      ].filter(Boolean);
      showNotification(createdBits.length ? `Quotation approved · ${createdBits.join(' · ')}` : (result?.release_pending ? 'Quotation approved · fulfilment is waiting for its configured release trigger' : 'Quotation approved'));
    } catch (error) {
      showNotification(error?.response?.data?.error || 'Failed to approve quotation', 'error');
    } finally {
      setApproving(false);
    }
  }

  /* ---------------------------------------
     UI
  --------------------------------------- */
  if (loading) {
    return (
      <>
        <div className="quotation-loading">Loading…</div>
      </>
    )
  }

  if (!quotation) {
    return (
      <>
        <div className="quotation-loading">Quotation not found</div>
      </>
    )
  }

  const workspaceRecord = {
    ...quotation,
    customer_name: customerName,
    customer_email: selectedLead?.email || '',
    customer_phone: selectedLead?.phone_number || selectedLead?.phone || '',
    customer_company: selectedLead?.company_name || '',
    total_display: formatMoney(totals.grandTotal),
  }
  const workspaceFields = [
    { key: 'status', label: 'Status', type: 'status', readOnly: true },
    { key: 'lead_id', label: 'Contact', type: 'select', required: true, options: leads.map((lead) => ({ value: lead.id, label: [lead.first_name, lead.last_name].filter(Boolean).join(' ') || lead.company_name || lead.email || `Contact #${lead.id}` })), readOnly: isLocked },
    { key: 'customer_company', label: 'Company', readOnly: true },
    { key: 'customer_email', label: 'Email', readOnly: true },
    { key: 'customer_phone', label: 'Phone', readOnly: true },
    { key: 'quotation_date', label: 'Quotation date', type: 'date', required: true, readOnly: isLocked },
    { key: 'valid_until', label: 'Valid until', type: 'date', required: true, readOnly: isLocked },
    { key: 'notes', label: 'Notes', type: 'textarea', readOnly: isLocked },
    { key: 'event_name', label: 'Event name', required: true, readOnly: isLocked },
    { key: 'event_start_date', label: 'Start date', type: 'date', required: true, readOnly: isLocked },
    { key: 'event_start_time', label: 'Start time', type: 'time', required: true, readOnly: isLocked },
    { key: 'event_end_date', label: 'End date', type: 'date', required: true, readOnly: isLocked },
    { key: 'event_end_time', label: 'End time', type: 'time', required: true, readOnly: isLocked },
    { key: 'event_location', label: 'Venue', required: true, readOnly: isLocked },
    { key: 'pax', label: 'PAX', type: 'number', required: true, readOnly: isLocked },
    { key: 'total_display', label: 'Grand total', readOnly: true },
  ]
  const workspaceCards = [
    { id: 'quotation-client', title: 'Client details', position: 'left', deletable: false, editable: !isLocked, fieldKeys: ['lead_id', 'customer_company', 'customer_email', 'customer_phone'], headerActions: () => !isLocked ? <button type="button" className="record-card-header-link" onClick={() => setOpenAddLeadDialog(true)}>+</button> : null },
    { id: 'quotation-details', title: 'Quotation details', position: 'left', deletable: false, editable: !isLocked, fieldKeys: ['quotation_date', 'valid_until', 'notes'] },
    ...(!isGeneralBusiness && quotation.quotation_mode === 'CATERING' ? [
      { id: 'quotation-event', title: 'Event details', position: 'left', deletable: false, editable: !isLocked, fieldKeys: ['event_name', 'event_start_date', 'event_start_time', 'event_end_date', 'event_end_time', 'event_location', 'pax'] },
      { id: 'quotation-pricing-basis', title: 'Guest packages', position: 'right', deletable: false, editable: false, fieldKeys: [], customContent: () => <QuotationPackageManager packages={packages} setPackages={setPackages} items={items} setItems={setItems} eventPax={pax} currency={currency} gstPricingMode={gstPricingMode} isLocked={isLocked} /> },
    ] : []),
    { id: 'quotation-actions', title: 'Document actions', position: 'right', fixed: true, disableDrag: true, deletable: false, allowSettings: false, titleEditable: false, fieldKeys: [], customContent: () => <div className="record-action-buttons"><button type="button" className="hs-listing__create" title="Save quotation" aria-label="Save quotation" disabled={isLocked || !isDirty} onClick={handleSubmit}><SaveOutlined /></button><button type="button" className="hs-listing__create" title={downloadingPdf ? "Downloading PDF…" : "Download PDF"} aria-label={downloadingPdf ? "Downloading PDF" : "Download PDF"} disabled={downloadingPdf} onClick={handleDownloadPdf}>{downloadingPdf ? <span className="record-action-loading" aria-hidden="true">…</span> : <PictureAsPdfOutlined />}</button>{quotation.status === 'pending' && !isLocked ? <button type="button" className="hs-listing__create" title="Approve quotation" aria-label="Approve quotation" onClick={openApprovalDialog}><CheckCircleOutline /></button> : null}<button type="button" className="hs-listing__create" title="Create new version" aria-label="Create new version" onClick={handleCreateVersion}><FileCopyOutlined /></button>{['approved', 'converted'].includes(String(quotation.status || '').toLowerCase()) ? <button type="button" className="hs-listing__create" title="Generate invoice" aria-label="Generate invoice" onClick={openInvoiceDialog}><ReceiptLongOutlined /></button> : null}<button type="button" className="hs-listing__create" title="Share quotation" aria-label="Share quotation" onClick={() => setChannelModalOpen(true)}><ShareOutlined /></button></div> },
    { id: 'quotation-related', title: 'Related documents', position: 'right', deletable: false, editable: false, fieldKeys: [], customContent: () => {
      const docs = [
        ...(quotation.orders || []).map((row) => ({ key: `order-${row.id}`, label: row.order_number || `Order #${row.id}`, meta: row.status, path: `/orders/${row.id}` })),
        ...(quotation.proforma_invoices || []).map((row) => ({ key: `pi-${row.id}`, label: row.proforma_number || `Proforma #${row.id}`, meta: row.status, path: `/proforma-invoices/${row.id}` })),
        ...(quotation.invoices || []).map((row) => ({ key: `inv-${row.id}`, label: row.invoice_number || `Invoice #${row.id}`, meta: row.status, path: `/invoices/${row.id}` })),
        ...(quotation.kots || []).map((row) => ({ key: `kot-${row.id}`, label: `KOT #${row.id}`, meta: row.status, path: `/kots?range=all&focusKotId=${row.id}` })),
        ...(quotation.deliveries || []).map((row) => ({ key: `delivery-${row.id}`, label: `Delivery #${row.id}`, meta: row.status, path: `/deliveries?focusDeliveryId=${row.id}` })),
      ]
      return <div className="quotation-related-documents">{docs.length ? docs.map((doc) => <button type="button" key={doc.key} className="quotation-related-document" onClick={() => navigate(doc.path)}><span>{doc.label}</span><CrmBadge tone="neutral">{doc.meta || 'Open'}</CrmBadge></button>) : <p className="quotation-related-empty">No related records yet.</p>}</div>
    } },
    { id: 'quotation-total', title: 'Quotation total', position: 'right', deletable: false, editable: false, fieldKeys: ['total_display'] },
  ]

  const saveWorkspaceFields = async (patch) => {
    const headerKeys = ['lead_id', 'quotation_date', 'valid_until', 'notes']
    const eventKeys = ['event_name', 'event_start_date', 'event_start_time', 'event_end_date', 'event_end_time', 'event_location', 'pax']
    const headerPatch = Object.fromEntries(Object.entries(patch).filter(([key]) => headerKeys.includes(key)))
    const eventPatch = Object.fromEntries(Object.entries(patch).filter(([key]) => eventKeys.includes(key)))
    await updateQuotation(id, { ...headerPatch, ...eventPatch })
    setHeaderForm((current) => ({ ...current, ...headerPatch }))
    setCateringMeta((current) => ({ ...current, ...eventPatch }))
    if (Object.prototype.hasOwnProperty.call(eventPatch, 'pax')) setPax(Number(eventPatch.pax) || 1)
    await loadQuotation()
  }

  const addSelectedProducts = (selectedProducts, packageKey = null) => {
    const added = selectedProducts.map((product) => ({
      product,
      product_id: product.id ?? product.product_id ?? null,
      product_name: product.name ?? product.product_name ?? product.label ?? product.title ?? '[Unnamed Product]',
      variant_id: product.variant_id ?? product.variant?.id ?? null,
      variant_sku: product.variant_sku ?? product.sku ?? product.variant?.sku ?? '',
      quantity: 1,
      cost_price: Number(product.cost_price ?? product.cost ?? 0) || 0,
      cost_price_qty: Number(product.cost_price_qty ?? 1) || 1,
      cost_price_unit: product.cost_price_unit || 'unit',
      cost_unit: product.cost_unit || product.cost_price_unit || 'unit',
      cost_pricing_mode: normalizeCostMode(product.cost_pricing_mode),
      cost_discount_percent: Number(product.cost_discount_percent ?? 0) || 0,
      selling_price: Number(product.selling_price ?? product.price ?? 0) || 0,
      selling_price_qty: Number(product.selling_price_qty ?? 1) || 1,
      selling_price_unit: product.selling_price_unit || 'unit',
      gst_rate: Number(product.gst_rate ?? 0) || 0,
      attributes_json: product.attributes_json || {},
      packaging_json: product.packaging_json || {},
      discount: 0,
      tax: 0,
      line_total: Number(product.selling_price ?? product.price ?? 0) || 0,
      package_key: packageKey,
    }))
    setItems((current) => [...current, ...added])
    setOpenProductDialog(false)
  }

  return (
    <>
      <SingleRecordWorkspace
        storageKey={`jdh-crm:record:quotation:${id}`}
        backTo="/quotations"
        backLabel="Quotations"
        title={quotation.quotation_number || `Quotation #${id}`}
        record={workspaceRecord}
        fields={workspaceFields}
        initialCards={workspaceCards}
        summaryCard={{ backTo: '/quotations', backLabel: 'Quotations', displayTitle: quotation.quotation_number || `Quotation #${id}`, subtitle: customerName, fieldKeys: ['status', 'quotation_date', 'valid_until'], editable: false }}
        hidePageHeader
        onSaveFields={saveWorkspaceFields}
        centerLabel={`Order Lines (${items.length})`}
        centerContent={<div className="quotation-order-lines-workspace"><QuotationItemsSection items={items} setItems={setItems} updateItem={updateItem} handleProductSelect={handleProductSelect} addItem={addItem} onAddProducts={() => setOpenProductDialog(true)} reorderItems={reorderItems} gstPricingMode={gstPricingMode} isLocked={isLocked} products={products} packages={packages} /></div>}
        centerSummary={<QuotationSummary totals={totals} overallDiscount={overallDiscount} setOverallDiscount={setOverallDiscount} currency={currency} isLocked={isLocked} />}
      />

      <QuotationProductPickerDrawer
        open={openProductDialog}
        products={products}
        packages={packages}
        onClose={() => setOpenProductDialog(false)}
        onAdd={addSelectedProducts}
      />

      <ChannelSelectModal
        open={channelModalOpen}
        onClose={() => setChannelModalOpen(false)}
        title={`Share Quotation ${quotation?.quotation_number || ''} with ${customerName}`}
        subtitle={briefSummary}
        defaultEmail
        defaultWhatsApp
        confirmLabel="Share Quotation"
        onConfirm={async ({ sendEmail = true, sendWhatsApp = false }) => {
          setChannelModalOpen(false)
          if (sendEmail) {
            await handleSendQuotationEmail()
          }
          if (sendWhatsApp) {
            await handleSendQuotationWhatsApp()
          }
        }}
      />

      <CrmModal
        open={invoiceDialog.open}
        onClose={invoiceDialog.loading ? undefined : () => setInvoiceDialog({ open: false, proforma: true, tax: false, loading: false })}
        title="Generate invoice documents"
        subtitle="Create one or both billing documents without leaving this quotation. Generated records appear in Related documents."
        size="sm"
        footer={<><CrmButton variant="secondary" disabled={invoiceDialog.loading} onClick={() => setInvoiceDialog({ open: false, proforma: true, tax: false, loading: false })}>Cancel</CrmButton><CrmButton variant="primary" loading={invoiceDialog.loading} onClick={generateSelectedInvoices}>Generate</CrmButton></>}
      >
        <div className="quotation-invoice-options">
          <CrmCheckbox label="Proforma Invoice" checked={invoiceDialog.proforma} onChange={(event) => setInvoiceDialog((current) => ({ ...current, proforma: event.target.checked }))} />
          <CrmCheckbox label="Tax Invoice" checked={invoiceDialog.tax} onChange={(event) => setInvoiceDialog((current) => ({ ...current, tax: event.target.checked }))} />
          <small>Tax Invoice is generated from the unified Order created by quotation approval.</small>
        </div>
      </CrmModal>

      <CrmModal
        open={approvalDialog.open}
        onClose={approving ? undefined : () => setApprovalDialog({ open: false, loading: false, preview: null })}
        title="Approve quotation?"
        subtitle="Approval is operational: downstream records are controlled by the resolved fulfilment profile."
        size="md"
        footer={<><CrmButton variant="secondary" disabled={approving} onClick={() => setApprovalDialog({ open: false, loading: false, preview: null })}>Cancel</CrmButton><CrmButton variant="primary" loading={approving} disabled={approvalDialog.loading || Boolean(approvalDialog.preview?.error)} onClick={confirmApproval}>Approve quotation</CrmButton></>}
      >
        {approvalDialog.loading ? <p className="crm-secondary-text">Resolving operational workflow…</p> : approvalDialog.preview?.error ? <p className="crm-error-text">{approvalDialog.preview.error}</p> : <div className="quotation-approval-preview"><div className="quotation-approval-preview__profile"><span>Fulfilment profile</span><strong>{approvalDialog.preview?.profile?.name || 'No active profile resolved'}</strong><small>{approvalDialog.preview?.profile?.release_condition ? `Release condition: ${approvalDialog.preview.profile.release_condition.replace(/_/g, ' ')}` : 'Approval will still create the unified Order record.'}</small></div><div className="quotation-approval-preview__effects">{(approvalDialog.preview?.effects || []).map((effect) => <div key={effect.key} className={effect.happens ? 'will-happen' : 'will-wait'}><span>{effect.happens ? '✓' : '•'}</span><div><strong>{effect.label}</strong><small>{effect.detail || (effect.happens ? 'Triggered by this approval' : 'Not triggered at approval')}</small></div></div>)}</div></div>}
      </CrmModal>

      <CrmModal
        open={Boolean(pendingNavigation)}
        onClose={() => setPendingNavigation(null)}
        title="Unsaved quotation changes"
        subtitle="You changed this quotation after the last save."
        size="sm"
        footer={<><CrmButton variant="secondary" onClick={() => setPendingNavigation(null)}>Stay</CrmButton><CrmButton variant="tertiary" onClick={() => leaveQuotationPage()}>Discard changes</CrmButton><CrmButton variant="primary" onClick={async () => { const destination = pendingNavigation; const saved = await handleSubmit(); if (saved) leaveQuotationPage(destination); }}>Save & leave</CrmButton></>}
      ><p className="crm-secondary-text">Save your changes before leaving, or discard them and continue.</p></CrmModal>

      <NotificationSnackbar
        open={notif.open}
        message={notif.message}
        severity={notif.severity}
        onClose={() => setNotif({ ...notif, open: false })}
      />
      <AddLeadDialog
        open={openAddLeadDialog}
        onClose={() => setOpenAddLeadDialog(false)}
        prefillName=""
        onLeadCreated={(lead) => {
          setLeads((p) => [...p, lead])
          setSelectedLead(lead)
          setHeaderForm((p) => ({ ...p, lead_id: lead.id }))
        }}
      />
    </>
  )
}

export default QuotationDetail
